import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import SeoHead from "@/components/SeoHead";
import FooterScripts from "@/components/FooterScripts";
import Footer from "@/components/Footer";
import { Download, ArrowLeft } from "lucide-react";

const TOPICS = [
  {
    title: "Crypto Theft & Ransomware",
    body: "How Bitcoin is actually traced after a hack or ransom payment. Why \"anonymous\" cryptocurrency isn't anonymous to a chain forensics specialist.",
  },
  {
    title: "Legacy Bitcoin Infrastructure (2010–2020)",
    body: "The forgotten era of Bitcoin: P2SH, early Bitcoin Core wallets, mining-pool history. Why old wallets are uniquely recoverable — and uniquely fragile.",
  },
  {
    title: "Crypto in Divorce & Family-Law Discovery",
    body: "Tracing comingled marital crypto through mixers and bridges. What courts can and cannot compel from a non-custodial wallet.",
  },
  {
    title: "Corporate Fraud & Insider Tracing",
    body: "Cross-exchange attribution, KYC pivots, bridge-hop and mixer-demixing forensics. Chain-of-custody protocols that survive litigation.",
  },
  {
    title: "Industry Commentary",
    body: "Explaining mainstream crypto news (hacks, ransoms, government seizures) with technical authority for a general audience.",
  },
];

const FORMAT_OPTIONS = ["In-studio", "Remote video", "Phone interview", "Written quote", "Recorded podcast"];

export default function MediaContact() {
  const [seo, setSeo] = useState(null);
  const [appearances, setAppearances] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", outlet: "", email: "", phone: "", role: "",
    topic: "", segment_date: "", format: "", audience_size: "", notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/seo/public`).then((r) => setSeo({
      ...r.data,
      page_title: "Media Contact — Sauderton Forensics · John Sauderton",
      meta_description: "Book John Sauderton for media appearances on crypto theft, blockchain forensics, ransomware tracing, and digital asset litigation.",
    })).catch(() => {});
    axios.get(`${API}/media-appearances`).then((r) => setAppearances(r.data || [])).catch(() => setAppearances([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.outlet || !form.topic) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/media-inquiries`, form);
      toast.success("Booking inquiry filed.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1729] text-white" data-testid="media-page">
      <SeoHead seo={seo} />

      {/* Top rail */}
      <div className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="media-brand">
            <div className="w-6 h-6 border border-white/70 relative">
              <div className="absolute inset-1 bg-white/90" />
            </div>
            <span className="mono-label text-white/90">SAUDERTON · FORENSICS</span>
          </Link>
          <Link to="/" className="mono-label hover:text-white inline-flex items-center gap-2" data-testid="media-back-home">
            <ArrowLeft className="w-3 h-3" /> RETURN TO INDEX
          </Link>
        </div>
      </div>

      {/* Hero strip */}
      <section className="border-b border-slate-800/60 grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="mono-label mb-6">§ MEDIA · BOOKINGS</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            For producers, segment editors,
            and reporters on a deadline.
          </h1>
          <p className="text-slate-300 mt-8 max-w-2xl text-base leading-relaxed">
            John Sauderton is available for on-camera, remote, and written commentary on crypto
            theft, ransomware tracing, blockchain forensics, and the legacy-era origins of Bitcoin.
            Booking turnaround is typically under 24 hours. After-hours / breaking-news requests are accepted.
          </p>
        </div>
      </section>

      {/* Press kit + bio (paper) */}
      <section className="paper-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-5">
              <div className="border border-paperline p-1 bg-paper">
                <img
                  src="https://customer-assets.emergentagent.com/job_blockchain-audit-10/artifacts/zde5d3tv_1778197290565.png"
                  alt="John Sauderton — press headshot"
                  className="w-full aspect-square object-cover"
                  data-testid="media-headshot"
                />
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={`${API}/press-kit.zip`}
                  className="btn-paper-primary inline-flex items-center justify-center gap-2"
                  data-testid="press-kit-download"
                >
                  <Download className="w-4 h-4" /> Download press kit (.zip)
                </a>
                <a
                  href="https://customer-assets.emergentagent.com/job_blockchain-audit-10/artifacts/zde5d3tv_1778197290565.png"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-paper-secondary inline-flex items-center justify-center gap-2"
                  data-testid="headshot-download"
                >
                  <Download className="w-4 h-4" /> High-res headshot
                </a>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <div className="mono-label-paper mb-6">SHORT BIO · APPROVED FOR PRESS</div>
              <h2 className="font-serif-display text-4xl leading-[1.1]">John Sauderton</h2>
              <div className="mono-label-paper mt-4">TECHNICAL CONSULTANT · DIGITAL ASSET LITIGATION</div>

              <div className="mt-8 space-y-5 text-paperink/85 text-base leading-relaxed">
                <p>
                  John Sauderton is a technical consultant for high-stakes digital asset litigation
                  and a specialist in the legacy era of Bitcoin (2010–2020). His expertise is derived
                  from <span className="text-paperink font-medium">20+ years of full-stack development
                  and blockchain systems analysis</span>. John is a Bitcoin O.G. — an original cypherpunk
                  from the very first days of Bitcoin&apos;s existence.
                </p>
                <p>
                  His engagements support law firms, private investigators, and family-law counsel
                  with Technical Forensic Mapping — reconstructing wallet provenance, tracing
                  comingled flows through legacy mixers and bridge hops, and producing auditable
                  work-product for matrimonial discovery, estate matters, and fraud recovery.
                </p>
                <p className="font-serif-display italic text-2xl text-paperink pt-2">
                  &quot;If it&apos;s hidden — John will find it.&quot;
                </p>
              </div>

              <div className="mt-8 border-t border-paperline pt-6 grid grid-cols-2 gap-y-3 text-sm">
                <div className="mono-label-paper">MEDIA CONTACT</div>
                <div className="font-mono">media@sauderton.com</div>
                <div className="mono-label-paper">WEB</div>
                <div className="font-mono">sauderton.com</div>
                <div className="mono-label-paper">TURNAROUND</div>
                <div className="font-mono">&lt; 24 HOURS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="paper-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="mono-label-paper mb-6">§ TOPICS · ON THE RECORD</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">What John speaks on.</h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-paperline border border-paperline">
            {TOPICS.map((t) => (
              <div key={t.title} className="bg-paper p-8" data-testid={`topic-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h3 className="font-serif-display text-2xl">{t.title}</h3>
                <p className="text-paperink/75 text-sm mt-3 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past appearances — only renders if any */}
      {appearances.length > 0 && (
        <section className="paper-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
            <div className="mono-label-paper mb-6">§ APPEARANCES · ARCHIVE</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">Recent on-record commentary.</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-paperline border border-paperline">
              {appearances.map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="bg-paper p-8 hover:bg-paperhover transition-colors block">
                  <div className="mono-label-paper">{a.outlet}</div>
                  <h3 className="font-serif-display text-xl mt-3">{a.title}</h3>
                  <div className="mono-label-paper text-paperink/50 mt-3">{a.air_date}</div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking form */}
      <section className="paper-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-4">
              <div className="mono-label-paper mb-6">§ BOOKING · INQUIRY</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">
                File a booking request.
              </h2>
              <p className="text-paperink/75 mt-8 text-sm leading-relaxed">
                Use this form for media bookings only. Case feasibility inquiries should
                go through the main case-intake page.
              </p>
              <div className="mt-10 mono-label-paper text-paperink/60">
                BREAKING-NEWS DESK · 24/7
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7 lg:col-start-6">
              <div className="border border-paperline bg-paper p-8 lg:p-12">
                {submitted ? (
                  <div className="text-center py-12" data-testid="media-submitted">
                    <div className="mono-label-paper">FILED</div>
                    <h3 className="font-serif-display text-3xl mt-4">Booking inquiry received.</h3>
                    <p className="text-paperink/75 mt-4 max-w-md mx-auto">
                      Confirmation will be sent to the email on file. For breaking-news segments,
                      mark your email subject with [URGENT] and we&apos;ll respond within the hour.
                    </p>
                  </div>
                ) : (
                <form onSubmit={submit} className="space-y-8" data-testid="media-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                    <FieldP label="Producer / Reporter Name *">
                      <input className="input-paper" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="m-name" />
                    </FieldP>
                    <FieldP label="Outlet *">
                      <input className="input-paper" required placeholder="CNN, Fox, Reuters, NYT…" value={form.outlet} onChange={(e) => update("outlet", e.target.value)} data-testid="m-outlet" />
                    </FieldP>
                    <FieldP label="Email *">
                      <input type="email" className="input-paper" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="m-email" />
                    </FieldP>
                    <FieldP label="Phone (for breaking news)">
                      <input className="input-paper" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="m-phone" />
                    </FieldP>
                    <FieldP label="Role">
                      <input className="input-paper" placeholder="Producer, Booker, Reporter…" value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="m-role" />
                    </FieldP>
                    <FieldP label="Format">
                      <select className="input-paper" value={form.format} onChange={(e) => update("format", e.target.value)} data-testid="m-format">
                        <option value="">— Select —</option>
                        {FORMAT_OPTIONS.map((c) => (<option key={c} value={c}>{c}</option>))}
                      </select>
                    </FieldP>
                    <FieldP label="Segment Date / Window">
                      <input className="input-paper" placeholder="e.g. Tonight 6pm ET, or Tue Mar 4" value={form.segment_date} onChange={(e) => update("segment_date", e.target.value)} data-testid="m-date" />
                    </FieldP>
                    <FieldP label="Audience Size">
                      <input className="input-paper" placeholder="Approx weekly viewers / listeners" value={form.audience_size} onChange={(e) => update("audience_size", e.target.value)} data-testid="m-audience" />
                    </FieldP>
                  </div>

                  <FieldP label="Topic / Story Angle *">
                    <textarea
                      className="input-paper"
                      required
                      placeholder="What's the story? Any specific angle or technical question for John?"
                      value={form.topic}
                      onChange={(e) => update("topic", e.target.value)}
                      data-testid="m-topic"
                    />
                  </FieldP>

                  <FieldP label="Notes / Requirements">
                    <textarea
                      className="input-paper"
                      placeholder="Travel? Studio location? Sound check window? Pre-interview required?"
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      data-testid="m-notes"
                    />
                  </FieldP>

                  <div className="pt-4 border-t border-paperline flex justify-end">
                    <button type="submit" disabled={submitting} className="btn-paper-primary" data-testid="m-submit">
                      {submitting ? "Filing…" : "File booking inquiry"}
                    </button>
                  </div>
                </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FooterScripts html={seo?.footer_html} />
    </div>
  );
}

function FieldP({ label, children }) {
  return (
    <label className="block">
      <span className="mono-label-paper block mb-3">{label}</span>
      {children}
    </label>
  );
}
